import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {useSelector} from '../../reduxHooks';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card } from '@betareader/design-system';

import {
  accessUtil,
  authUtil,
  featureToggleUtil,
  uxAnalyticsUtil,
} from '../../utils';
import { CenteredContent } from 'src/style';
import BetaIcon from 'src/components/BetaIcon';
import Icon from 'src/components/AppIcon';
import useEditorFeedback, {
  EditorClassification,
  EditorFeedbackReport,
  EditorFeedbackStatus,
} from 'src/hooks/useEditorFeedback';
import { useCredits, useCreditsConfig } from 'src/hooks/useCredits';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import CreditBalanceChip from 'src/components/credits/CreditBalanceChip';
import OutOfCreditsModal from 'src/components/credits/OutOfCreditsModal';
import { downloadEditorReportPdf } from 'src/utils/editorReportPdf';

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  sv: 'Swedish',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  no: 'Norwegian',
  da: 'Danish',
  fi: 'Finnish',
  nl: 'Dutch',
  it: 'Italian',
};
const languageName = (code?: string) =>
  code ? LANGUAGE_NAMES[code] || code : '';

const MyCenteredContent = styled(CenteredContent)`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  flex: 1;
  font-size: 1.5em;
  font-weight: bold;
  margin: 0;
`;

const VoiceCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Free teaser: a taste of the opening, sitting between the persona and the
// (paid) run CTA. Deliberately opening-only — the upsell names what the full
// whole-manuscript report adds.
const TeaserCard = styled(Card)`
  border-left: 3px solid #2b8a8a !important;
  background: var(--br-raised-surface) !important;

  strong {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  ul {
    margin: 8px 0;
    padding-left: 1.2em;
  }
  li {
    margin-bottom: 4px;
  }
  em {
    color: var(--br-muted);
    font-size: 0.92em;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;

  .br-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    border: 1px solid var(--br-border);
    border-radius: 4px;
    background: var(--br-raised-surface);
  }

  .br-chip button {
    border: 0;
    background: transparent;
    cursor: pointer;
    font: inherit;
  }

  input {
    min-height: 34px;
    padding: 4px 8px;
    border: 1px solid var(--br-border);
    border-radius: var(--br-control-radius);
  }
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  label {
    font-weight: 600;
    font-size: 0.9em;
  }

  select {
    min-height: 40px;
    padding: 6px 10px;
    border: 1px solid var(--br-border);
    border-radius: var(--br-control-radius);
    background: var(--br-surface);
    color: var(--br-text);
  }
`;

// ── report styling per the claude.ai/design "AI Editor" comp ──
const Masthead = styled.div`
  background: linear-gradient(135deg, #12303f, #1c4b53);
  color: #fff;
  padding: 26px 30px;
  border-radius: 14px;
`;

const MastheadEyebrow = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #9fd6d0;
`;

const StatLine = styled.div`
  display: flex;
  gap: 18px;
  margin-top: 14px;
  flex-wrap: wrap;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);

  b {
    color: #fff;
    font-weight: 800;
  }
`;

const SectionCard = styled.div<{ $open?: boolean }>`
  background: var(--br-surface);
  border: 1px solid ${props => (props.$open ? '#f0b862' : '#e3e6ea')};
  border-radius: 14px;
  margin-top: 14px;
  overflow: hidden;
`;

const SectionHead = styled.button`
  width: 100%;
  background: var(--br-surface);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  font-size: 15.5px;
  font-weight: 800;
  color: var(--br-text);
  text-align: left;
  font-family: inherit;
`;

const SectionTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionIcon = styled.span<{ $bg: string; $color: string }>`
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: ${props => props.$bg};
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 900;
`;

const CountPill = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #8a9099;
  background: var(--br-raised-surface);
  padding: 2px 9px;
  border-radius: 20px;
`;

const Chevron = styled.span<{ $open?: boolean }>`
  font-size: 13px;
  color: #b0b6bd;
  transition: transform 0.2s;
  transform: rotate(${props => (props.$open ? '0deg' : '-90deg')});
`;

const SectionBody = styled.div`
  padding: 2px 18px 18px;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--br-muted);
`;

const FixBox = styled.div`
  background: var(--br-raised-surface);
  border: 1px solid #ffe1b8;
  border-radius: 11px;
  padding: 14px 16px;
  margin-top: 12px;
`;

const FixCap = styled.div`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #b8690a;
  margin-bottom: 8px;
`;

const FixRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
`;

const FixNum = styled.span`
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ff8c00;
  color: #fff;
  font-weight: 900;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FixText = styled.span`
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--br-text);
`;

const ItemCard = styled.div`
  border: 1px solid #e7eaed;
  border-radius: 11px;
  padding: 13px 15px;
  margin-bottom: 12px;
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 7px;
`;

const RankBadge = styled.span`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #141d2b;
  color: #fff;
  font-weight: 900;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemTitle = styled.span`
  font-weight: 800;
  font-size: 14.5px;
  flex: 1;
  color: var(--br-text);
`;

const SEVERITY_COLORS: Record<string, [string, string]> = {
  critical: ['#fdecec', '#c0392b'],
  major: ['#fff3e2', '#c26f00'],
  minor: ['#f2f4f7', '#7b828b'],
};

const SevChip = styled.span<{ $level: string }>`
  background: ${props =>
    (SEVERITY_COLORS[props.$level] || SEVERITY_COLORS.minor)[0]};
  color: ${props =>
    (SEVERITY_COLORS[props.$level] || SEVERITY_COLORS.minor)[1]};
  font-weight: 800;
  font-size: 10.5px;
  letter-spacing: 0.05em;
  padding: 3px 9px;
  border-radius: 6px;
  text-transform: uppercase;
`;

const QuoteRow = styled.div`
  display: flex;
  gap: 9px;
  margin-top: 6px;
`;

const QuoteText = styled.span`
  font-style: italic;
  border-left: 2px solid #e3e6ea;
  padding-left: 9px;
  font-size: 13px;
  color: var(--br-muted);
`;

const RoleCap = styled.div`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #8a9099;
  margin: 14px 0 8px;
`;

const FooterNote = styled.p`
  color: #8a9099;
  font-size: 13px;
  margin-top: 1.5rem;
  line-height: 1.6;
`;

const CATEGORY_OPTIONS = [
  'picture-book',
  'chapter-book',
  'middle-grade',
  'YA',
  'adult',
].map(v => ({ key: v, value: v, text: v }));

const POV_OPTIONS = [
  'first-person',
  'third-limited',
  'third-omniscient',
  'second-person',
].map(v => ({ key: v, value: v, text: v }));

const RUNNING_STATUSES: EditorFeedbackStatus[] = [
  'pending',
  'classifying',
  'analyzing',
  'verifying',
];

const PHASES: EditorFeedbackStatus[] = [
  'classifying',
  'analyzing',
  'verifying',
  'completed',
];

// Chips editor for a string[] classification field.
const ChipList: React.FC<{
  values: string[];
  onChange: (next: string[]) => void;
}> = ({ values, onChange }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');

  const add = () => {
    const value = input.trim();
    if (value && !values.includes(value)) {
      onChange([...values, value]);
    }
    setInput('');
  };

  return (
    <Chips>
      {values.map(value => (
        <span key={value} className="br-chip">
          {value}
          <button
            type="button"
            aria-label={`${t('Remove', 'Remove')} ${value}`}
            onClick={() => onChange(values.filter(v => v !== value))}
          >×</button>
        </span>
      ))}
      <input
        aria-label={t('AddDots', 'Add…')}
        value={input}
        placeholder={t('AddDots', 'Add…')}
        onChange={event => setInput(event.target.value)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter') {
            add();
          }
        }}
      />
      <Button variant="secondary" size="small" onClick={add} aria-label={t('Add', 'Add')}>+</Button>
    </Chips>
  );
};

/**
 * The rendered editorial report (masthead + collapsible sections). Extracted
 * so the exact same renderer backs both the live report and the static sample
 * page — no drift. `onDownload` omitted (e.g. the sample) hides the download
 * button; `creditsCharged` omitted hides the usage note.
 */
export const EditorReportView: React.FC<{
  report: EditorFeedbackReport;
  creditsCharged?: number;
  onDownload?: () => void;
}> = ({ report, creditsCharged, onDownload }) => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({
    0: true,
    1: true,
  });
  const toggleSection = (index: number) =>
    setOpenSections(prev => ({ ...prev, [index]: !prev[index] }));

  return (
    <>
      <Masthead>
        <HeaderRow>
          <MastheadEyebrow>
            {t('EditorReportComplete', 'EDITOR REPORT · COMPLETE')} · AI-GENERATED
          </MastheadEyebrow>
          {onDownload && (
            <Button
              size="small"
              variant="secondary"
              onClick={onDownload}
              style={{ marginLeft: 'auto' }}
            >{t('DownloadPDF', 'Download PDF')}</Button>
          )}
        </HeaderRow>
        <StatLine>
          <span>
            <b>{(report.plotHoles || []).length}</b>{' '}
            {t('PlotHoles', 'plot holes')}
          </span>
          <span>
            <b>{(report.unusedSetups || []).length}</b>{' '}
            {t('UnusedSetups', 'unused setups')}
          </span>
          <span>
            <b>
              {(report.characters || []).reduce(
                (sum, group) => sum + (group.members?.length || 0),
                0
              )}
            </b>{' '}
            {t('Characters', 'characters')}
          </span>
          <span>
            <b>{(report.continuityNotes || []).length}</b>{' '}
            {t('ContinuityNotes', 'continuity notes')}
          </span>
          {!!creditsCharged && (
            <span style={{ marginLeft: 'auto' }}>
              {t('ThisReportUsedNCredits', 'Used {{n}} credits', {
                n: creditsCharged,
              })}
            </span>
          )}
        </StatLine>
      </Masthead>

      {/* Summarizing picture */}
      <SectionCard $open={!!openSections[0]}>
        <SectionHead onClick={() => toggleSection(0)}>
          <SectionTitle>
            <SectionIcon $bg="#eef2f7" $color="#41505f">
              ✦
            </SectionIcon>
            {t('SummarizingPicture', 'Summarizing picture')}
          </SectionTitle>
          <Chevron $open={!!openSections[0]}>▼</Chevron>
        </SectionHead>
        {!!openSections[0] && (
          <SectionBody>
            <p>{report.summary}</p>
            {!!(report.fixFirst || []).length && (
              <FixBox>
                <FixCap>
                  {t('FixBeforeNextVersion', 'FIX BEFORE THE NEXT VERSION')}
                </FixCap>
                {(report.fixFirst || []).map(item => (
                  <FixRow key={item.n}>
                    <FixNum>{item.n}</FixNum>
                    <FixText>{item.text}</FixText>
                  </FixRow>
                ))}
              </FixBox>
            )}
          </SectionBody>
        )}
      </SectionCard>

      {/* Plot holes */}
      <SectionCard $open={!!openSections[1]}>
        <SectionHead onClick={() => toggleSection(1)}>
          <SectionTitle>
            <SectionIcon $bg="#fdecec" $color="#c0392b">
              !
            </SectionIcon>
            {t('PlotHolesAndGaps', 'Plot holes & logical gaps')}
            <CountPill>
              {(report.plotHoles || []).length} · {t('Ranked', 'ranked')}
            </CountPill>
          </SectionTitle>
          <Chevron $open={!!openSections[1]}>▼</Chevron>
        </SectionHead>
        {!!openSections[1] && (
          <SectionBody>
            {(report.plotHoles || []).map(hole => (
              <ItemCard key={hole.rank}>
                <ItemHead>
                  <RankBadge>{hole.rank}</RankBadge>
                  <ItemTitle>{hole.title}</ItemTitle>
                  <SevChip $level={hole.severity}>{hole.severity}</SevChip>
                </ItemHead>
                <div>{hole.detail}</div>
                {(hole.quotes || []).map((q, i) => (
                  <QuoteRow key={i}>
                    <CountPill>{q.ch}</CountPill>
                    <QuoteText>&ldquo;{q.text}&rdquo;</QuoteText>
                  </QuoteRow>
                ))}
              </ItemCard>
            ))}
          </SectionBody>
        )}
      </SectionCard>

      {/* Characters */}
      <SectionCard $open={!!openSections[2]}>
        <SectionHead onClick={() => toggleSection(2)}>
          <SectionTitle>
            <SectionIcon $bg="#eafbfa" $color="#00867e">
              ☺
            </SectionIcon>
            {t('Characters', 'Characters')}
            <CountPill>
              {(report.characters || []).reduce(
                (sum, group) => sum + (group.members?.length || 0),
                0
              )}
            </CountPill>
          </SectionTitle>
          <Chevron $open={!!openSections[2]}>▼</Chevron>
        </SectionHead>
        {!!openSections[2] && (
          <SectionBody>
            {(report.characters || []).map(group => (
              <div key={group.role}>
                <RoleCap>{group.role}</RoleCap>
                {(group.members || []).map(member => (
                  <ItemCard key={member.name}>
                    <ItemHead>
                      <ItemTitle>{member.name}</ItemTitle>
                      {member.cites && <CountPill>{member.cites}</CountPill>}
                    </ItemHead>
                    {member.traits && (
                      <div>
                        <b>{t('Traits', 'Traits')}</b>: {member.traits}
                      </div>
                    )}
                    {member.motivation && (
                      <div>
                        <b>{t('Motivation', 'Motivation')}</b>:{' '}
                        {member.motivation}
                      </div>
                    )}
                    {member.arc && (
                      <div>
                        <b>{t('Arc', 'Arc')}</b>: {member.arc}
                      </div>
                    )}
                  </ItemCard>
                ))}
              </div>
            ))}
          </SectionBody>
        )}
      </SectionCard>

      {/* Act structure */}
      <SectionCard $open={!!openSections[3]}>
        <SectionHead onClick={() => toggleSection(3)}>
          <SectionTitle>
            <SectionIcon $bg="#eef2f7" $color="#41505f">
              ▤
            </SectionIcon>
            {t('ActStructure', 'Act structure')}
          </SectionTitle>
          <Chevron $open={!!openSections[3]}>▼</Chevron>
        </SectionHead>
        {!!openSections[3] && (
          <SectionBody>
            {(report.acts || []).map(act => (
              <ItemCard key={act.name}>
                <ItemHead>
                  <ItemTitle>{act.name}</ItemTitle>
                  {act.chapters && <CountPill>{act.chapters}</CountPill>}
                </ItemHead>
                {act.note && <div>{act.note}</div>}
              </ItemCard>
            ))}
          </SectionBody>
        )}
      </SectionCard>

      {/* Unused possibilities */}
      <SectionCard $open={!!openSections[4]}>
        <SectionHead onClick={() => toggleSection(4)}>
          <SectionTitle>
            <SectionIcon $bg="#fff3e2" $color="#c26f00">
              ◎
            </SectionIcon>
            {t('UnusedPossibilities', 'Unused possibilities')}
            <CountPill>
              {(report.unusedSetups || []).length} ·{' '}
              {t('ChekhovsGuns', "Chekhov's guns")}
            </CountPill>
          </SectionTitle>
          <Chevron $open={!!openSections[4]}>▼</Chevron>
        </SectionHead>
        {!!openSections[4] && (
          <SectionBody>
            {(report.unusedSetups || []).map((setup, i) => (
              <ItemCard key={i}>
                <ItemHead>
                  <ItemTitle>{setup.title}</ItemTitle>
                  <CountPill>{setup.ch}</CountPill>
                </ItemHead>
                {setup.note && <div>{setup.note}</div>}
              </ItemCard>
            ))}
          </SectionBody>
        )}
      </SectionCard>

      {/* Continuity notes */}
      <SectionCard $open={!!openSections[5]}>
        <SectionHead onClick={() => toggleSection(5)}>
          <SectionTitle>
            <SectionIcon $bg="#f2f4f7" $color="#7b828b">
              ≈
            </SectionIcon>
            {t('MinorContinuityNotes', 'Minor continuity notes')}
            <CountPill>{(report.continuityNotes || []).length}</CountPill>
          </SectionTitle>
          <Chevron $open={!!openSections[5]}>▼</Chevron>
        </SectionHead>
        {!!openSections[5] && (
          <SectionBody>
            {(report.continuityNotes || []).map((note, i) => (
              <QuoteRow key={i}>
                <CountPill>{note.ch}</CountPill>
                <span>{note.text}</span>
              </QuoteRow>
            ))}
          </SectionBody>
        )}
      </SectionCard>

      <FooterNote>
        {t(
          'EditorFooterNote',
          'Every finding is observational and cited to the manuscript — the AI Editor identifies gaps and unused opportunities, it does not tell you what to write.'
        )}
      </FooterNote>
    </>
  );
};

const BookEditorFeedback: React.FC = () => {
  const { t, i18n } = useTranslation();

  const currentBook = useSelector((state: any) => state.book.currentBook);
  const userProfile = useSelector((state: any) => state.user.userProfile);

  const contentList = currentBook?.content || [];
  const latestContentId = contentList.at(-1)?.id;
  const [selectedContentId, setSelectedContentId] = useState<
    string | undefined
  >(latestContentId);

  const contentId = selectedContentId || latestContentId;

  const {
    editorFeedbackQuery,
    startEditorFeedback,
    isStarting,
    classify,
    isClassifying,
    saveClassification,
    isSaving,
  } = useEditorFeedback({ bookId: currentBook?._id, contentId });

  const { data: credits } = useCredits();
  const { data: config } = useCreditsConfig();
  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const aiEditorEnabled =
    flags?.ai_editor === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'ai_editor');

  const [isEditingVoice, setIsEditingVoice] = useState(false);
  const [draftClassification, setDraftClassification] =
    useState<EditorClassification | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const editorFeedback = editorFeedbackQuery.data?.editorFeedback;
  const status = editorFeedback?.status;
  const classification = editorFeedback?.classification;

  const canEdit = useCallback(
    () => accessUtil.isAllowed(currentBook?.access, ['edit']),
    [currentBook?.access]
  );

  // Price and length limit must use the selected manuscript version.
  const estimatedWordCount = useMemo(() => {
    const version = contentList.find((c: any) => c.id === contentId);
    return (version?.parts || []).reduce(
      (sum: number, part: any) => sum + (part.wordCount || 0),
      0
    );
  }, [contentList, contentId]);

  // A report is priced in credits, scaled by length: a rate per 10k words with
  // a floor. Compute the cost for this book, then gate on the credit balance.
  const per10k = config?.features?.editor_feedback?.creditsPer10kWords;
  const minCredits = config?.features?.editor_feedback?.minCredits ?? 0;
  const runCost = useMemo(() => {
    if (per10k === undefined) return undefined;
    return Math.max(minCredits, Math.ceil(estimatedWordCount / 10000) * per10k);
  }, [per10k, minCredits, estimatedWordCount]);
  const balance = credits?.balance ?? 0;
  const hasEnough = runCost === undefined || balance >= runCost;

  // Manuscripts above the size ceiling can't be analysed in one run.
  const maxEditorWords = config?.maxEditorWords;
  const tooLong =
    maxEditorWords !== undefined && estimatedWordCount > maxEditorWords;

  const startRun = useCallback(
    async (isRerun: boolean) => {
      uxAnalyticsUtil.trackEvent({
        category: 'AI',
        action: isRerun ? 'clicked-rerun-editor' : 'clicked-run-editor',
      });
      await authUtil.getFreshIdToken();
      const result = await startEditorFeedback({
        ...(classification ? { classification } : {}),
        // report language = the user's selected app language
        reportLanguage: (i18n.language || 'en').split('-')[0],
      });
      if (result?.status === 402) {
        setModalOpen(true);
        return;
      }
      editorFeedbackQuery.refetch();
    },
    [startEditorFeedback, classification, editorFeedbackQuery, i18n.language]
  );

  const beginEditVoice = () => {
    setDraftClassification(classification || {});
    setIsEditingVoice(true);
  };

  const saveVoice = async () => {
    if (draftClassification) {
      await saveClassification(draftClassification);
      editorFeedbackQuery.refetch();
    }
    setIsEditingVoice(false);
  };

  const detectVoice = async () => {
    const result = await classify();
    if (result?.classification) {
      setDraftClassification(result.classification);
    }
  };

  const downloadPdf = () => {
    const report = editorFeedback?.report;
    if (!report) {
      return;
    }
    downloadEditorReportPdf({
      report,
      classification: editorFeedback?.classification,
      bookTitle: currentBook?.title || 'Editor report',
      creditsCharged: editorFeedback?.creditsCharged,
      completedAt: editorFeedback?.completedAt,
      t,
    });
  };

  if (!currentBook) {
    return null;
  }

  if (!flagsLoading && !aiEditorEnabled) {
    return (
      <MyCenteredContent>
        <Card>
          {t('FeatureNotAvailable', 'This feature is not available.')}
        </Card>
      </MyCenteredContent>
    );
  }

  if (!canEdit()) {
    return (
      <MyCenteredContent>
        <Card>{t('ActionCancelled')}</Card>
      </MyCenteredContent>
    );
  }

  const isRunning = !!status && RUNNING_STATUSES.includes(status);
  const isCompleted = status === 'completed';
  const isError = status === 'error';
  const report = editorFeedback?.report;

  const activeClassification = isEditingVoice
    ? draftClassification || {}
    : classification || {};

  const updateDraft = (patch: Partial<EditorClassification>) =>
    setDraftClassification(prev => ({ ...(prev || {}), ...patch }));

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${t('AIEditor', 'AI Editor')} · ${currentBook.title}`}</title>
      </Helmet>
      <MyCenteredContent>
        <HeaderRow>
          <Title>
            {t('AIEditor', 'AI Editor')} <BetaIcon />
          </Title>
          <CreditBalanceChip usageHref="/account/subscription" />
        </HeaderRow>

        {/* Content version selector */}
        <FieldRow style={{ marginTop: '1rem' }}>
          <label htmlFor="editor-content-version">{t('Version', 'Version')}</label>
          <select
            id="editor-content-version"
            value={contentId}
            onChange={event => setSelectedContentId(event.target.value)}
          >
            {contentList.map((c: any, i: number) => (
              <option key={c.id} value={c.id}>{c.title || `${t('Version', 'Version')} ${i + 1}`}</option>
            ))}
          </select>
        </FieldRow>

        {/* BetaBot editor voice card */}
        <VoiceCard>
          <HeaderRow>
            <strong>{t('BetaBotEditorVoice', 'BetaBot editor voice')}</strong>
            {!isEditingVoice ? (
              <Button
                size="small"
                variant="secondary"
                onClick={beginEditVoice}
                style={{ marginLeft: 'auto' }}
              >{t('Edit', 'Edit')}</Button>
            ) : (
              <Button
                size="small"
                aria-busy={isClassifying}
                onClick={detectVoice}
                style={{ marginLeft: 'auto' }}
              >{t('Detect', 'Detect')}</Button>
            )}
          </HeaderRow>
          <em>
            {t(
              'EditorVoiceDescription',
              'Reads your manuscript as a meticulous, sharp {{persona}} editor. Tuned automatically from your book settings and a scan of the opening chapters.',
              {
                persona: [
                  activeClassification.category,
                  (activeClassification.genres || []).join('/'),
                  languageName(activeClassification.language),
                  activeClassification.normCreative
                    ? t('NormCreative', 'norm-creative')
                    : null,
                ]
                  .filter(Boolean)
                  .join(' '),
              }
            )}
          </em>

          {isEditingVoice && (
            <div>
              <FieldRow>
                <label htmlFor="editor-category">{t('Category', 'Category')}</label>
                <select id="editor-category" value={activeClassification.category || ''}
                  onChange={event => updateDraft({ category: event.target.value })}>
                  <option value="">—</option>
                  {CATEGORY_OPTIONS.map(option => <option key={option.key} value={option.value}>{option.text}</option>)}
                </select>
              </FieldRow>
              <FieldRow>
                <label htmlFor="editor-pov">{t('NarrativePOV', 'Narrative POV')}</label>
                <select id="editor-pov" value={activeClassification.narrativePOV || ''}
                  onChange={event => updateDraft({ narrativePOV: event.target.value })}>
                  <option value="">—</option>
                  {POV_OPTIONS.map(option => <option key={option.key} value={option.value}>{option.text}</option>)}
                </select>
              </FieldRow>
              <FieldRow>
                <label htmlFor="editor-norm-creative">{t('NormCreative', 'Creative norm')}</label>
                <input id="editor-norm-creative" type="checkbox"
                  checked={!!activeClassification.normCreative}
                  onChange={event => updateDraft({ normCreative: event.target.checked })}
                />
              </FieldRow>
              <FieldRow>
                <label>{t('Genres', 'Genres')}</label>
                <ChipList
                  values={activeClassification.genres || []}
                  onChange={next => updateDraft({ genres: next })}
                />
              </FieldRow>
              <FieldRow>
                <label>{t('Tone', 'Tone')}</label>
                <ChipList
                  values={activeClassification.tone || []}
                  onChange={next => updateDraft({ tone: next })}
                />
              </FieldRow>
              <FieldRow>
                <label>{t('Themes', 'Themes')}</label>
                <ChipList
                  values={activeClassification.themes || []}
                  onChange={next => updateDraft({ themes: next })}
                />
              </FieldRow>
              <Button
                size="small"
                aria-busy={isSaving}
                onClick={saveVoice}
              >{t('Save', 'Save')}</Button>
              <Button
                size="small"
                variant="secondary"
                onClick={() => setIsEditingVoice(false)}
              >{t('Cancel', 'Cancel')}</Button>
            </div>
          )}
        </VoiceCard>

        {/* Free teaser: opening observations + upsell (before a report exists) */}
        {!isEditingVoice &&
          !report &&
          (editorFeedback?.teaserObservations?.length ?? 0) > 0 && (
            <TeaserCard>
              <strong>
                <Icon name="lightning" />
                {t('BetaBotNoticed', 'BetaBot noticed in your opening')}
              </strong>
              <ul>
                {editorFeedback!.teaserObservations!.map((obs, i) => (
                  <li key={i}>{obs}</li>
                ))}
              </ul>
              <em>
                {t(
                  'EditorTeaserUpsell',
                  'A free glimpse of your opening. The full report analyses structural issues, unpaid setups, and character arcs across your whole manuscript.'
                )}
              </em>
            </TeaserCard>
          )}

        {/* Idle: Run CTA */}
        {!isRunning && !isCompleted && (
          <Card>
            {tooLong ? (
              <Alert variant="warning">
                {t(
                  'ManuscriptTooLong',
                  'This manuscript is too long for a single analysis ({{n}} words; the limit is {{max}}). Split it into parts and analyse each separately.',
                  {
                    n: estimatedWordCount.toLocaleString(),
                    max: (maxEditorWords ?? 0).toLocaleString(),
                  }
                )}
              </Alert>
            ) : hasEnough ? (
              <Button
                aria-busy={isStarting}
                onClick={() => startRun(false)}
              >{t(
                  'RunFullAnalysis',
                  'Run full analysis — {{n}} credits',
                  { n: runCost ?? '' }
                )}</Button>
            ) : (
              <div>
                <p>
                  {t(
                    'NotEnoughCredits',
                    'Not enough credits — you have {{balance}}, a report costs {{required}}.',
                    { balance, required: runCost }
                  )}
                </p>
                <Button onClick={() => setModalOpen(true)}>{t('SeeOptions', 'See options')}</Button>
              </div>
            )}
            <p style={{ marginTop: '8px', color: '#666' }}>
              {t(
                'EditorRunDescription',
                'Per-chapter extraction → synthesis → verification. Takes ~4 minutes for a novel.'
              )}
            </p>
            {per10k !== undefined && (
              <p style={{ color: '#888', fontSize: '0.92em' }}>
                {t(
                  'EditorPricedByLength',
                  'Priced by length — {{rate}} credits per 10,000 words ({{words}} words in this book).',
                  {
                    rate: per10k,
                    words: estimatedWordCount.toLocaleString(),
                  }
                )}
              </p>
            )}
            <a href="/editorial-report-sample" target="_blank" rel="noreferrer">
              {t('SeeASampleReport', 'See a sample report first →')}
            </a>
          </Card>
        )}

        {/* Running */}
        {isRunning && (
          <Card role="status">
            <Icon name="spinner" loading />
            {t(
              'AnalyzingBackground',
              "Analyzing '{{title}}' — background job, you can leave this page and come back",
              { title: currentBook.title }
            )}
            <ol style={{ marginTop: '1rem' }}>
              {PHASES.filter(p => p !== 'completed').map(phase => {
                const phaseIndex = PHASES.indexOf(phase);
                const currentIndex = status ? PHASES.indexOf(status) : -1;
                return (
                  <li
                    key={phase}
                    aria-current={status === phase ? 'step' : undefined}
                  >
                    {currentIndex > phaseIndex && <Icon name="check" />}
                    {t(phase, phase)}
                  </li>
                );
              })}
            </ol>
            {status === 'analyzing' && editorFeedback?.progress && (
              <p>
                {t('ChapterXofY', 'Chapter {{done}} of {{total}}', {
                  done: editorFeedback.progress.chaptersDone,
                  total: editorFeedback.progress.totalChapters,
                })}
              </p>
            )}
            <p style={{ marginTop: '8px', color: '#999', fontSize: '0.9em' }}>
              {t(
                'EditorRunSelfHeals',
                "If it stalls, it resets automatically and any reserved credits are refunded — you're only charged for a completed report."
              )}
            </p>
          </Card>
        )}

        {/* Error — always a generic, user-friendly message. The raw
            provider/internal error is kept server-side (logs + COGS), never
            surfaced to authors. */}
        {isError && (
          <Alert variant="error">
            <Icon name="exclamation triangle" />
            {t(
              'EditorAnalysisFailed',
              "The analysis couldn't be completed. Your credits were not charged — please try again in a little while."
            )}
            <div style={{ marginTop: '8px' }}>
              <Button
                size="small"
                onClick={() => startRun(false)}
              >{t('Retry', 'Retry')}</Button>
            </div>
          </Alert>
        )}

        {/* Completed */}
        {isCompleted && report && (
          <>
            <EditorReportView
              report={report}
              creditsCharged={editorFeedback?.creditsCharged}
              onDownload={downloadPdf}
            />

            <Button
              variant="secondary"
              aria-busy={isStarting}
              disabled={!hasEnough}
              onClick={() => startRun(true)}
            >{t(
                'RerunOnThisVersion',
                'Re-run on this version — {{n}} credits',
                { n: runCost ?? '' }
              )}</Button>
          </>
        )}
      </MyCenteredContent>
      <OutOfCreditsModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default BookEditorFeedback;
